import {
    ApiResultListCliArgumentConfigurationView,
    CliArgumentConfigurationView,
    CliArgumentEnumType,
    CliClientActionCodeGenerationWrapper,
    CliExecutionView,
    CompleteClientAgencyEventView,
    CompleteClientUnitView,
    GenerateRenCliClientAction,
    OpenDexPersonFormCliClientAction,
    OpenDexResultsCliClientAction,
    OpenDexVehicleFormCliClientAction,
    ResyncCliClientAction,
    ViewEventHistoryCliClientAction,
    ViewUnitHistoryCliClientAction,
    LogoutCliClientAction,
    OpenBoloFormCliClientAction,
    ShowOnMapCliClientAction,
    ViewEventCliClientAction,
    EditEventCliClientAction,
    AgencyAssistanceCliClientAction,
    EditUnitCliClientAction,
    OpenRotationForEventCliClientAction,
    OpenAlertBannerCliClientAction,
    SelfInitiateCliClientAction,
    UpdateEventLocationCliClientAction,
    OpenEditHospitalFormCliClientAction,
} from '@mark43/cad-api';
import { CommandEntityMap } from './command-line-editor-store';
import { SegmentTypeEnumType } from './constants';
import { CommandStatusEnumType } from './types/command-status-enum';

export const commandLineArgumentTypeEnum = {
    TOKEN: 'TOKEN',
    COMMAND: 'COMMAND',
    FORM_IDENTIFIER: 'FORM_IDENTIFIER',
} as const;

export const CliExecutionResponseTokenTypeEnum = {
    AGENCY_EVENT: {
        name: 'AGENCY_EVENT',
    },
    COMMENT: {
        name: 'COMMENT',
    },
    LOCATION: {
        name: 'LOCATION',
    },
    MESSAGING: {
        name: 'MESSAGING',
    },
    TEXT: {
        name: 'TEXT',
    },
    UNIT: {
        name: 'UNIT',
    },
    UNIT_STATE: {
        name: 'UNIT_STATE',
    },
} as const;

export type CliExecutionResponseTokenTypeEnumType = keyof typeof CliExecutionResponseTokenTypeEnum;

interface BaseResponseToken {
    type: CliExecutionResponseTokenTypeEnumType;
    text: string;
}

interface AgencyEventResponseToken extends BaseResponseToken {
    type: 'AGENCY_EVENT';
    data: { id: number; agencyEventNumber: string };
}

interface UnitResponseToken extends BaseResponseToken {
    type: 'UNIT';
    data: { callSign: string };
}

interface TextResponseToken extends BaseResponseToken {
    type: 'TEXT';
}

interface CommentResponseToken extends BaseResponseToken {
    type: 'COMMENT';
}

interface UnitStateResponseToken extends BaseResponseToken {
    type: 'UNIT_STATE';
    data: { displayValue: string };
}

interface LocationResponseToken extends BaseResponseToken {
    type: 'LOCATION';
    data: { id: number };
}

interface MessagingResponseToken extends BaseResponseToken {
    type: 'MESSAGING';
    data: {
        conversationId: number;
        userMessageId: number;
    };
}

export type CliExecutionResponseToken =
    | AgencyEventResponseToken
    | UnitResponseToken
    | TextResponseToken
    | CommentResponseToken
    | UnitStateResponseToken
    | LocationResponseToken
    | MessagingResponseToken;

export interface CommandLineHistoryView {
    id: number;
    successText?: string | CliExecutionResponseToken[];
    startTime: string;
    endTime: string;
    cliArgumentConfigurationId: number;
    hidden?: boolean;
    commandText: string;
    status: CommandStatusEnumType;
    errors?: string;
    executionView?: CliExecutionView;
    commandEntityMap?: CommandEntityMap;
    partialSuccessMessage?: string;
}

interface BaseSegment {
    segmentType: SegmentTypeEnumType;
    start: number;
    length: number;
}

export interface CommandSegment extends BaseSegment {
    segmentType: 'COMMAND';
    segmentData: CliArgumentConfigurationView;
}

export interface CommentSegment extends BaseSegment {
    segmentType: 'COMMENT';
    segmentData: string;
}

export interface LocationSegment extends BaseSegment {
    segmentType: 'LOCATION';
    segmentData: string;
}

export interface DataExchangeInquiryParametersSegment extends BaseSegment {
    segmentType: 'DATA_EXCHANGE_INQUIRY_PARAMETERS';
    segmentData: string;
}

export interface OriAliasSegment extends BaseSegment {
    segmentType: 'ORI_ALIAS';
    segmentData: string;
}

export interface EventSegment extends BaseSegment {
    segmentType: 'EVENT';
    segmentData: string;
}

export interface HospitalSegment extends BaseSegment {
    segmentType: 'HOSPITAL';
    segmentData: string;
}

export interface HighPrioritySegment extends BaseSegment {
    segmentType: 'IS_HIGH_PRIORITY';
    segmentData: string;
}

export interface UnitSegment extends BaseSegment {
    segmentType: 'UNIT';
    segmentData: string;
}

export interface LicensePlateSegment extends BaseSegment {
    segmentType: 'LICENSE_PLATE';
    segmentData: string;
}

export interface MessageRecipientSegment extends BaseSegment {
    segmentType: 'MESSAGE_RECIPIENT';
    segmentData: string;
}

export interface FormFieldSegment extends BaseSegment {
    segmentType:
        | 'DISPATCH_AREA'
        | 'MEMBER'
        | 'VEHICLE'
        | 'CALL_FOR_SERVICE'
        | 'DISPOSITION'
        | 'AGENCY'
        | 'DISPATCH_AREA_GROUP'
        | 'STATION'
        | 'MESSAGE_GROUP'
        | 'CALL_TAKER_STATION'
        | 'UNIT_TAG'
        | 'TIME'
        | 'MESSAGE_RECIPIENT';
    segmentData: string;
}

export type Segment =
    | CommandSegment
    | CommentSegment
    | LocationSegment
    | DataExchangeInquiryParametersSegment
    | FormFieldSegment
    | EventSegment
    | UnitSegment
    | HighPrioritySegment
    | LicensePlateSegment
    | HospitalSegment
    | OriAliasSegment
    | MessageRecipientSegment;

export type ParserCommand = { alias: string; [index: string]: unknown };

export interface ParserConfig {
    commands: Record<string, CliArgumentConfigurationView>;
    cliArgumentForArgumentDefinition: Record<string, CliArgumentEnumType>;
    acceptTemporaryUnitOnSelfInitiateCommand?: boolean;
    licensePlateToken?: string;
    highPriorityToken?: string;
    locationToken?: string;
    dexInquiryParamsToken?: string;
    eventToken?: string;
    formFieldSeparator?: string;
    formFieldDelimiter?: string;
}

export interface CommandLineExecutionResponseWithActions {
    agencyEventIds: number[];
    agencyEvents: CompleteClientAgencyEventView[];
    cliExecutionResponseTokens: CliExecutionResponseToken[];
    partialSuccessMessage?: string;
    unitIds: number[];
    units: CompleteClientUnitView[];
    versionErrorMessage?: string;
}

export interface ApiResultCommandLineExecutionResponseWithActions {
    data?: CommandLineExecutionResponseWithActions;
    error?: string;
    success: boolean;
}

export interface ExecuteCommandLineConfigRequest {
    method: 'GET';
    path: 'cli';
    returns: ApiResultListCliArgumentConfigurationView;
}

export type CommandLineClientActionUnionType = CliClientActionCodeGenerationWrapper[keyof CliClientActionCodeGenerationWrapper];

export function isEditEventCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is EditEventCliClientAction {
    return clientAction.cliClientActionType === 'EDIT_EVENT';
}

export function isOpenEditHospitalFormCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is OpenEditHospitalFormCliClientAction {
    return clientAction.cliClientActionType === 'OPEN_EDIT_HOSPITAL_FORM';
}

export function isEditUnitCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is EditUnitCliClientAction {
    return clientAction.cliClientActionType === 'EDIT_UNIT';
}

export function isGenerateRenCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is GenerateRenCliClientAction {
    return clientAction.cliClientActionType === 'GENERATE_REN';
}

export function isLogoutCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is LogoutCliClientAction {
    return clientAction.cliClientActionType === 'LOGOUT';
}

export function isOpenBoloFormCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is OpenBoloFormCliClientAction {
    return clientAction.cliClientActionType === 'OPEN_BOLO_FORM';
}

export function isOpenDexPersonFormCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is OpenDexPersonFormCliClientAction {
    return clientAction.cliClientActionType === 'OPEN_DEX_PERSON_FORM';
}

export function isOpenDexResultsCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is OpenDexResultsCliClientAction {
    return clientAction.cliClientActionType === 'OPEN_DEX_RESULTS';
}

export function isOpenDexVehicleFormCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is OpenDexVehicleFormCliClientAction {
    return clientAction.cliClientActionType === 'OPEN_DEX_VEHICLE_FORM';
}

export function isViewEventCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is ViewEventCliClientAction {
    return clientAction.cliClientActionType === 'VIEW_EVENT';
}

export function isShowOnMapCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is ShowOnMapCliClientAction {
    return clientAction.cliClientActionType === 'SHOW_ON_MAP';
}

export function isAgencyAssistanceCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is AgencyAssistanceCliClientAction {
    return clientAction.cliClientActionType === 'AGENCY_ASSISTANCE';
}

export function isResyncCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is ResyncCliClientAction {
    return clientAction.cliClientActionType === 'RESYNC';
}

export function isViewEventHistoryCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is ViewEventHistoryCliClientAction {
    return clientAction.cliClientActionType === 'VIEW_EVENT_HISTORY';
}

export function isViewUnitHistoryCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is ViewUnitHistoryCliClientAction {
    return clientAction.cliClientActionType === 'VIEW_UNIT_HISTORY';
}

export function isOpenRotationForEventCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is OpenRotationForEventCliClientAction {
    return clientAction.cliClientActionType === 'OPEN_ROTATION_FOR_EVENT';
}

export function isOpenAlertBannerCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is OpenAlertBannerCliClientAction {
    return clientAction.cliClientActionType === 'OPEN_ALERT_BANNER';
}

export function isSelfInitiateCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is SelfInitiateCliClientAction {
    return clientAction.cliClientActionType === 'SELF_INITIATE';
}

export function isUpdateEventLocationCliClientAction(
    clientAction: CommandLineClientActionUnionType
): clientAction is UpdateEventLocationCliClientAction {
    return clientAction.cliClientActionType === 'UPDATE_EVENT_LOCATION';
}
